import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Code,
  CopyButton,
  Group,
  Loader,
  Modal,
  ScrollArea,
  Stack,
  Text
} from '@mantine/core'
import { useWriteStatusStore } from '@/protocols/persistence'
import { getWriteStore } from '@/protocols/persistence/writeStore'
import { useUploadStatusStore } from '@/protocols/persistence/useUploadStatusStore'
import { getUploadStore } from '@/protocols/persistence/uploadStore'
import type { PendingWrite } from '@/protocols/persistence/types'
import type { PendingUpload } from '@/protocols/persistence/uploadTypes'
import { PendingWritesTable } from './PendingWritesTable.tsx'
import { PendingUploadTable } from './PendingUploadTable'

interface Props {
  companyId: string
  protocolId: string
  /**
   * Called when the user clicks Complete Protocol in VIEWER mode
   * AND both stores are drained. ProtocolRecorder wires this to
   * stop the watch_worker via tauriService.stopDataCollection and
   * then dispatch the existing drain-mode open to fire the
   * completeAndNavigate flow. When this prop is omitted the
   * modal's viewer-mode Complete Protocol button has no effect
   * — only the drain-mode flow (driven by the dispatcher's
   * onResolved) remains available.
   */
  onCompleteProtocol?: () => void | Promise<void>
}

const SHOW_ACTIVITY_MODAL_EVENT = 'show-pending-activity-modal'
const SET_ACTIVITY_COMPLETE_ERROR_EVENT = 'set-pending-activity-complete-error'

const AUTO_REFRESH_INTERVAL_MS = 1000

// Same secondary-button state machine as the legacy PendingWritesTable.
// Updated inputs:
//   - drained: union of write + upload in-flight = 0
//   - hasDeadRows: union of write dead + upload dead > 0
type SecondaryConfig = {
  hidden?: boolean
  label: string
  color: 'blue' | 'red' | 'green'
  dataTestid: string
  disabled: boolean
  spinning: boolean
}

// Priority: retry → syncing → completed (hidden) → complete.
// The first three are static; only `complete` derives disabled/spinning
// from runtime state, so it stays inline in getSecondary.
const SECONDARY_RETRY_FAILURES: SecondaryConfig = {
  label: 'Retry Failures',
  color: 'red',
  dataTestid: 'drain-blocking-resolve-button',
  disabled: false,
  spinning: false,
}
const SECONDARY_SYNCING: SecondaryConfig = {
  label: 'Syncing',
  color: 'blue',
  dataTestid: 'drain-blocking-syncing-button',
  disabled: true,
  spinning: true,
}
// Hidden: no drain callback captured → modal opened from the tray for
// viewing; there's no protocol-completion action to offer.
const SECONDARY_COMPLETED: SecondaryConfig = {
  hidden: true,
  label: 'Completed',
  color: 'green',
  dataTestid: 'drain-blocking-completed-button',
  disabled: false,
  spinning: false,
}

function getSecondary(
  drained: boolean,
  hasDeadRows: boolean,
  isResolving: boolean,
  hasOnCompleteProtocol: boolean,
  state: DrainState | null
): SecondaryConfig {
  if (hasDeadRows) return SECONDARY_RETRY_FAILURES
  if (!drained) return SECONDARY_SYNCING
  if (!state?.cb) return SECONDARY_COMPLETED
  return {
    label: 'Complete Protocol',
    color: 'green',
    dataTestid: 'drain-blocking-continue-button',
    disabled: isResolving || !hasOnCompleteProtocol,
    spinning: isResolving,
  }
}

// Held by `drainStateRef`. Captured by the dispatcher on
// `show-pending-activity-modal` and consumed by `fireOnResolved`.
interface DrainState {
  cb: () => Promise<void>
  disableAutoResolve: boolean
}

interface EventDetail {
  mode?: 'viewer' | 'drain'
  onResolved?: () => Promise<void>
  /**
   * When true, the auto-resolver effect (drained + no dead → fire
   * onResolved) is disabled. Used by the Complete flow: the user
   * MUST explicitly click Continue to call onResolved.
   */
  disableAutoResolve?: boolean
  intent?: 'navigate' | 'complete'
}

// PendingActivityModal: the orchestrator/Modal that mounts both
// PendingWritesTable and PendingUploadTable.
//
// Responsibilities:
//   - Listen for `show-pending-activity-modal` (renamed from the
//     prior `show-pending-writes-table`).
//   - Drive the auto-refresh interval that re-fetches write rows
//     AND re-reads the upload-status store's already-1s-polled
//     `pendingUploads`.
//   - Implement the three-state Secondary button (Retry Failures
//     / Syncing / Complete Protocol) over the union of writes and
//     uploads, so the same workflow the legacy PendingWritesTable
//     exposed is preserved.
//   - Fire the new `onCompleteProtocol` prop when the user clicks
//     Complete Protocol in viewer mode + drained (the watch_worker-
//     stop hand-off lives in the caller).
export const PendingActivityModal: React.FC<Props> = ({
  companyId, protocolId, onCompleteProtocol,
}) => {
  const [opened, setOpened] = useState(false)
  const [writeRows, setWriteRows] = useState<PendingWrite[]>([])
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [mode, setMode] = useState<'viewer' | 'drain'>('viewer')
  const [completeError, setCompleteError] = useState<string | null>(null)
  const [autoRefreshOn, setAutoRefreshOn] = useState<boolean>(true)
  const [isResolving, setIsResolving] = useState<boolean>(false)

  // Why one ref for both fields: the click handler and the auto-
  // resolve effect both read these synchronously, atomically.
  const drainStateRef = useRef<DrainState | null>(null)
  // Ensures `onResolved` fires exactly once per dispatch (effect +
  // click both go through fireOnResolved).
  const firedRef = useRef(false)
  // Drives auto-open: fires on the 0 → >0 transition of the
  // combined deadCount (writes + uploads). Manually-closed modal
  // stays closed until deadCount cycles through 0 again.
  const prevDeadCountRef = useRef(0)

  const pendingCount = useWriteStatusStore((s) => s.pendingCount)
  const writeDeadCount = useWriteStatusStore((s) => s.deadCount)
  const hydrationError = useWriteStatusStore((s) => s.hydrationError)
  const clearDeadWrite = useWriteStatusStore((s) => s.clearDeadWrite)
  const markRetried = useWriteStatusStore((s) => s.markRetried)
  const retryHydration = useWriteStatusStore((s) => s.retryHydration)
  const uploadPendingUploads = useUploadStatusStore((s) => s.pendingUploads)
  const uploadSummary = useUploadStatusStore((s) => s.summary)
  const browserUploads = useUploadStatusStore((s) => s.browserUploads)

  // Combined uploads mirror PendingWritesTray: in-flight are the
  // four transient states (found/copied/pending/uploading). Persisted
  // is intentionally excluded — those rows are done.
  const uploadInFlight =
    uploadSummary.found + uploadSummary.copied +
    uploadSummary.pending + uploadSummary.uploading
  const uploadDeadCount = uploadSummary.dead

  // Browser-mode uploads don't pass through the Rust queue (summary is
  // zeros there). Count the in-flight + dead browserUploads entries so
  // the totals match the tray in both modes. (Dead browser entries are
  // also merged into uploadPendingUploads by the store, so they show up
  // in the table below with a working Retry.)
  let browserActive = 0
  let browserDead = 0
  browserUploads.forEach((e) => {
    if (e.status === 'in_flight') browserActive += 1
    else if (e.status === 'dead') browserDead += 1
  })

  const deadCount = writeDeadCount + uploadDeadCount + browserDead

  const refresh = useCallback(async () => {
    try {
      const rows = await getWriteStore().getPendingWrites({
        companyId, protocolId,
        stateFilter: ['pending', 'dead'],
        coalesceView: false,
      })
      setWriteRows(rows.sort((a, b) => a.createdAt - b.createdAt))
      setFetchError(null)
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      setFetchError(msg)
      console.error('[PendingActivityModal] refresh failed', e)
    }
  }, [companyId, protocolId])

  // Re-fetches write rows + re-polls both status stores. Every retry
  // path ends with this so the modal shows the post-retry state.
  const refreshAll = useCallback(async () => {
    await refresh()
    await useWriteStatusStore.getState().refresh(companyId, protocolId)
    await useUploadStatusStore.getState().refresh(companyId, protocolId)
  }, [refresh, companyId, protocolId])

  // Opens when deadCount transitions from zero → nonzero across
  // the union of writes + uploads.
  useEffect(() => {
    const wasZero = prevDeadCountRef.current === 0
    const isPositive = deadCount > 0
    if (isPositive && wasZero) setOpened(true)
    prevDeadCountRef.current = deadCount
  }, [deadCount - browserDead]) // we don't want to open just becaus browser dead count for uploads changes.

  // Refresh when opened or hydration error occurs.
  useEffect(() => {
    if (opened) refresh()
  }, [opened, hydrationError, refresh])

  // Auto-refresh interval. Polls both stores. The upload-status
  // store has its own 1 s bootstrap poll, so `uploadPendingUploads`
  // is already fresh — we just re-render whenever it changes.
  useEffect(() => {
    if (!opened || !autoRefreshOn) return
    const id = window.setInterval(() => {
      void refresh()
      void useWriteStatusStore.getState().refresh(companyId, protocolId)
        .catch((e) => console.error('[PendingActivityModal] auto-refresh failed', e))
    }, AUTO_REFRESH_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [opened, autoRefreshOn, refresh, companyId, protocolId])

  // Window events: open the modal (show-pending-activity-modal,
  // detail.mode defaults to 'viewer'; capture caller's onResolved
  // for fireOnResolved) and surface a complete-flow error
  // (set-pending-activity-complete-error).
  useEffect(() => {
    const onShow = (e: Event) => {
      const detail = (e as CustomEvent<EventDetail>).detail
      setMode(detail?.mode ?? 'viewer')
      drainStateRef.current = detail?.onResolved
        ? { cb: detail.onResolved, disableAutoResolve: !!detail.disableAutoResolve }
        : null
      firedRef.current = false
      // Do NOT clear completeError: the Complete flow's error
      // path dispatches set-pending-activity-complete-error
      // first and then re-opens the modal, expecting the error
      // to persist until the user successfully navigates away.
      setOpened(true)
    }
    const onCompleteError = (e: Event) => {
      const detail = (e as CustomEvent<{ message?: string }>).detail
      if (detail?.message) setCompleteError(detail.message)
    }
    window.addEventListener(SHOW_ACTIVITY_MODAL_EVENT, onShow)
    window.addEventListener(SET_ACTIVITY_COMPLETE_ERROR_EVENT, onCompleteError)
    return () => {
      window.removeEventListener(SHOW_ACTIVITY_MODAL_EVENT, onShow)
      window.removeEventListener(SET_ACTIVITY_COMPLETE_ERROR_EVENT, onCompleteError)
    }
  }, [])

  const finalizeRetry = (id: number) => {
    const dead = useWriteStatusStore.getState().deadWrites.find((d) => d.id === id)
    const writeKey = dead?.coalesceKey ?? dead?.stepId ?? null
    if (writeKey) markRetried(writeKey)
    clearDeadWrite(id)
  }

  const handleWriteRetry = async (row: PendingWrite) => {
    await getWriteStore().retryWrite(companyId, protocolId, row.id)
    finalizeRetry(row.id)
    await refreshAll()
  }

  const handleUploadRetry = async (row: PendingUpload) => {
    await getUploadStore().retryUpload(companyId, protocolId, row.id)
    await refreshAll()
  }

  // handleRetryAll routes to BOTH stores' bulk retry so a single
  // button covers write failures AND upload failures (per the user
  // spec Q3: "Retry Failures action is to retry both").
  const handleRetryAll = async () => {
    const [retriedWriteIds] = await Promise.all([
      getWriteStore().retryAllDead(companyId, protocolId),
      getUploadStore().retryAllDeadUploads(companyId, protocolId),
    ])
    for (const id of retriedWriteIds) finalizeRetry(id)
    await refreshAll()
  }

  const handleRetryHydration = async () => {
    await retryHydration(companyId, protocolId)
    await refresh()
  }

  const hasDeadRows = deadCount > 0
  const errorMessage = completeError ?? fetchError ?? hydrationError

  const draining = mode === 'drain'
  // "drained" now unions both stores: no in-flight pending writes
  // AND no in-flight uploads (including browser-mode direct uploads).
  // Combined with hasDeadRows (which also unions), the Secondary
  // button's three states respond to the full activity picture.
  const drained = pendingCount === 0 && uploadInFlight === 0 && browserActive === 0

  const fireOnResolved = useCallback(async () => {
    if (firedRef.current) return
    const state = drainStateRef.current
    if (!state) return
    setIsResolving(true)
    try {
      await state.cb()
      setOpened(false)
      firedRef.current = true
      drainStateRef.current = null
    } catch {
      // rejection observed; modal stays open so the user can retry
    } finally {
      setIsResolving(false)
    }
  }, [])

  // Auto-fire onResolved the first time the drain catches up.
  // Disabled by disableAutoResolve (Complete flow: user must click
  // Continue).
  useEffect(() => {
    if (!draining) return
    if (!drained) return
    if (hasDeadRows) return
    if (!drainStateRef.current) return
    if (drainStateRef.current.disableAutoResolve) return
    fireOnResolved()
  }, [draining, drained, hasDeadRows])

  const combinedFailures = writeDeadCount + uploadDeadCount
  const message: string | null = combinedFailures > 0
    ? `${combinedFailures} failed (waiting for intervention).`
    : errorMessage ? 'Could not load pending activity.' : null

  const hasOnCompleteProtocol = typeof onCompleteProtocol === 'function'
  const secondary = getSecondary(
    drained, hasDeadRows, isResolving, hasOnCompleteProtocol, drainStateRef.current
  )

  // Three arms:
  //   1. dead rows always trigger retry (both stores).
  //   2. drain mode + drained → existing fireOnResolved.
  //   3. viewer mode + drained + onCompleteProtocol → fire the
  //      caller's hook (which stops the watcher + dispatches
  //      drain mode). Without the prop wired, the third arm is
  //      a no-op so an unwired caller can't accidentally show
  //      a "Complete" button that does nothing.
  const handleSecondary = async () => {
    if (hasDeadRows) return handleRetryAll()
    if (draining && drained) return fireOnResolved()
    if (!draining && drained && hasOnCompleteProtocol) {
      return void onCompleteProtocol!()
    }
  }

  return (
    <Modal
      withinPortal={true}
      opened={opened}
      onClose={() => setOpened(false)}
      title={<Text fw="bold" size="1em" ta="center">Pending Write & Upload Activity</Text>}
      size="xl"
      data-testid="pending-activity-modal"
    >
      <Stack data-testid="pending-activity-modal-content">
        {draining && !drained && (
          <Text size="sm">
            All your changes must be saved before the protocol can be completed.
            If you need to skip this step then the server's view of your protocol will be
            incomplete until you resume this session and finish syncing the changes to
            the server.
          </Text>
        )}
        {draining && drained && hasDeadRows && (
          <Text size="sm" c="red">
            {deadCount} change{deadCount === 1 ? '' : 's'} could not be saved. Resolve
            them before continuing.
          </Text>
        )}

        <Group justify="space-between">
          {message && (
            <Text size="sm" data-testid="pending-activity-message">{message}</Text>
          )}
          <Button
            size="xs"
            miw="12em"
            variant={autoRefreshOn ? 'filled' : 'default'}
            onClick={() => setAutoRefreshOn((v) => !v)}
            data-testid="auto-refresh-toggle"
            rightSection={autoRefreshOn
              ? <Loader size="xs" color="var(--bios-strong-accent)" />
              : <Box size="xs">Off</Box>}
          >
            Auto-refresh
          </Button>
        </Group>

        {errorMessage && (
          <Alert color="red" data-testid={completeError ? 'complete-error' : 'hydration-error'}>
            <Group justify="space-between" align="flex-start">
              <Text size="sm" fw={500}>
                {completeError
                  ? 'Could not complete the protocol.'
                  : 'Could not load pending activity.'}
              </Text>
              <CopyButton value={errorMessage}>
                {({ copied, copy }) => (
                  <Button size="xs" variant="default" onClick={copy} data-testid="copy-error">
                    {copied ? 'Copied' : 'Copy error'}
                  </Button>
                )}
              </CopyButton>
            </Group>
            <Code block mt="xs" data-testid="error-message">
              {errorMessage}
            </Code>
            {!completeError && <Button
              size="xs"
              mt="xs"
              onClick={handleRetryHydration}
              data-testid="retry-hydration"
            > Retry hydration </Button>}
          </Alert>
        )}

        <ScrollArea p={"0.25em"} style={{ border: "var(--bios-border)" }} h={Math.max(writeRows.length, uploadPendingUploads.length) > 5 ? 220 : undefined}>
          <Box style={{ display: "grid", grid: "auto / 1fr", rowGap: "1em", }}>
            <PendingWritesTable rows={writeRows} onRetry={handleWriteRetry} />
            {/* Per user spec Q2: show ALL upload states (including
                        persisted) so the user can see what files have already
                        reached the server. Persisted rows render with a green
                        badge in PendingUploadTable. */}
            <PendingUploadTable
              rows={uploadPendingUploads}
              onRetry={handleUploadRetry}
            />
          </Box>
        </ScrollArea>

        {secondary.hidden ? <Box /> :
          <Button
            size="xs"
            color={secondary.color}
            onClick={handleSecondary}
            disabled={secondary.disabled}
            data-testid={secondary.dataTestid}
          >
            {secondary.label}
            {secondary.spinning &&
              <Loader ml="1em" size="xs" color="var(--bios-strong-accent)" style={{ marginRight: 8 }} />}
          </Button>
        }
      </Stack>
    </Modal>
  )
}
